import { useMemo, useState } from 'react'

import CheckIcon from '@/assets/icons/icon-check.svg'
import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { classNames } from '@/utils/helpers'

export interface MultiSelectOption {
  value: string
  label: string
  disabled?: boolean
  tooltip?: string
}

interface Props {
  options: MultiSelectOption[]
  placeholder?: string
  className?: string
  value?: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  searchPlaceholder?: string
  entityName: string
  triggerTestId?: string
  contentTestId?: string
  maxSelection?: number
}

export const CustomListFormMultiSelect = ({
  options,
  placeholder,
  className,
  value,
  onChange,
  disabled,
  searchPlaceholder = 'Search...',
  entityName,
  triggerTestId,
  contentTestId,
  maxSelection,
}: Props) => {
  const [open, setOpen] = useState(false)

  const isAllSelected = !!maxSelection && (value?.length ?? 0) >= maxSelection

  const triggerContent = useMemo(() => {
    if (isAllSelected) {
      return `Max ${entityName} selected`
    }
    if (value && value.length > 1) {
      return `${value.length} ${entityName} selected`
    }
    if (value && value.length === 1) {
      return options.find((option) => option.value === value[0])?.label
    }

    return placeholder
  }, [isAllSelected, value, placeholder, entityName, options])

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger
        asChild
        testId={triggerTestId}
      >
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={classNames('flex justify-between', className)}
          disabled={disabled}
        >
          <div className="flex gap-2 font-normal">{triggerContent}</div>
          <ExpandMoreIcon className="w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-100 w-full p-0"
        testId={contentTestId}
      >
        <Command>
          <CommandList>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandEmpty>No {entityName} found.</CommandEmpty>
            <CommandGroup className="max-h-44 overflow-auto">
              {options.map((option) => {
                const isSelected = value?.includes(option.value)
                const disabledByMaxSelection = isAllSelected && !isSelected
                const itemContent = (
                  <CommandItem
                    key={option.value}
                    disabled={option.disabled || disabledByMaxSelection}
                    className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                    onSelect={() => {
                      const newValue = isSelected
                        ? value?.filter((item) => item !== option.value)
                        : [...(value || []), option.value]

                      if (newValue) {
                        onChange(newValue)
                      }
                    }}
                  >
                    <div
                      className={classNames(
                        'border-primary-500 mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                        isSelected ? 'bg-primary-500 text-white' : 'opacity-50',
                      )}
                    >
                      {isSelected && <CheckIcon className="w-3" />}
                    </div>
                    {option.label}
                  </CommandItem>
                )

                return option.tooltip ? (
                  <Tooltip
                    key={option.value}
                    side="right"
                    content={option.tooltip}
                    className="block cursor-default"
                  >
                    {itemContent}
                  </Tooltip>
                ) : (
                  itemContent
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
